import { useEffect, useState } from 'react';
import {
  applyLeave,
  listLeaveBalances,
  listLeaveRequests,
  listLeaveTypes,
  reviewLeave,
  type LeaveBalance,
  type LeaveRequest,
  type LeaveType,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { StatusBadge } from '../components/StatusBadge';
import { Icon } from '../components/Icon';

export function Leaves() {

  const { session, can } = useAuth();
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Apply Modal
  const [applyModal, setApplyModal] = useState(false);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [totalDays, setTotalDays] = useState(1);
  const [reason, setReason] = useState('');

  // Review Modal
  const [reviewModal, setReviewModal] = useState(false);
  const [selectedReq, setSelectedReq] = useState<LeaveRequest | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'approved' | 'rejected'>('approved');
  const [reviewRemarks, setReviewRemarks] = useState('');

  async function loadData() {
    try {
      setLoading(true);
      const [typesRes, balancesRes, reqsRes] = await Promise.all([
        listLeaveTypes().catch(() => ({ data: [] })),
        listLeaveBalances().catch(() => ({ data: [] })),
        listLeaveRequests({ viewAll: can('leave.manage') || can('leave.approve') }).catch(() => ({ data: [] })),
      ]);
      setTypes(typesRes.data || []);
      setBalances(balancesRes.data || []);
      setRequests(reqsRes.data || []);
      if (typesRes.data?.length && !leaveTypeId) {
        setLeaveTypeId(typesRes.data[0].id);
      }
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load leave records' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, []);

  // Update total days automatically on date change
  useEffect(() => {
    if (startDate && endDate) {
      const s = new Date(startDate);
      const e = new Date(endDate);
      const diffTime = e.getTime() - s.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setTotalDays(diffDays > 0 ? diffDays : 1);
    }
  }, [startDate, endDate]);

  async function handleApply(e: React.FormEvent) {
    e.preventDefault();
    if (!leaveTypeId) return;
    try {
      await applyLeave({
        leaveTypeId,
        startDate,
        endDate,
        totalDays: Number(totalDays),
        reason,
      });
      setMsg({ type: 'success', text: 'Leave application submitted successfully!' });
      setApplyModal(false);
      setReason('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to submit leave application' });
    }
  }

  async function handleReview(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedReq) return;
    try {
      await reviewLeave(selectedReq.id, {
        status: reviewStatus,
        remarks: reviewRemarks || null,
      });
      setMsg({ type: 'success', text: `Leave request ${reviewStatus} successfully!` });
      setReviewModal(false);
      setSelectedReq(null);
      setReviewRemarks('');
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to review leave request' });
    }
  }

  const filteredRequests = requests.filter((r) => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="leaves" size="xs" />
            <span>Time Off & Attendance</span>
          </span>
          <h1>Leave Management</h1>
        </div>
        <button className="btn-primary" onClick={() => setApplyModal(true)}>
          <Icon name="plus" size="xs" />
          <span>Apply for Leave</span>
        </button>
      </div>

      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      {/* Leave Balances Cards */}
      <div className="stats-grid">
        {balances.map((b) => {
          const remaining = Math.max(0, b.allocated_days - b.used_days);
          return (
            <div key={b.id || b.leave_type_code} className="stat-card">
              <span className="stat-label">{b.leave_type_name} ({b.leave_type_code})</span>
              <span className="stat-value">{remaining} <span style={{ fontSize: 13, fontWeight: 'normal', color: 'var(--ink-faint)' }}>days left</span></span>
              <div className="stat-hint">
                Allocated: {b.allocated_days} · Used: {b.used_days} {b.pending_days > 0 && `· Pending: ${b.pending_days}`}
              </div>
            </div>
          );
        })}
      </div>

      {/* Leave Requests Table */}
      <div className="panel">
        <div className="panel-head">
          <strong>Leave Applications</strong>
          <div className="toolbar">
            <select value={filter} onChange={(e) => setFilter(e.target.value as any)}>
              <option value="all">All Statuses</option>
              <option value="pending">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="empty">Loading leave requests...</div>
        ) : (
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Applied On</th>
                  <th>Employee</th>
                  <th>Leave Type</th>
                  <th>Duration</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="empty">No leave requests found.</td>
                  </tr>
                ) : (
                  filteredRequests.map((r) => (
                    <tr key={r.id}>
                      <td className="mono">{r.created_at?.split('T')[0] || '—'}</td>
                      <td>
                        <strong>{r.first_name ? `${r.first_name} ${r.last_name || ''}` : session?.user.fullName}</strong>
                        <span className="muted mono" style={{ fontSize: 11, display: 'block' }}>{r.employee_code}</span>
                      </td>
                      <td>
                        <strong>{r.leave_type_name}</strong>
                      </td>
                      <td>
                        <span className="mono">{r.start_date}</span> to <span className="mono">{r.end_date}</span>
                        <span className="muted" style={{ display: 'block', fontSize: 11 }}>({r.total_days} {r.total_days === 1 ? 'day' : 'days'})</span>
                      </td>
                      <td>{r.reason}</td>
                      <td>
                        <StatusBadge status={r.status} />
                        {r.review_remarks && <span className="muted" style={{ display: 'block', fontSize: 11 }}>Note: {r.review_remarks}</span>}
                      </td>
                      <td>
                        {r.status === 'pending' && (can('leave.manage') || can('leave.approve') || can('organization.manage')) ? (
                          <button
                            className="btn-quiet btn-sm"
                            onClick={() => {
                              setSelectedReq(r);
                              setReviewStatus('approved');
                              setReviewModal(true);
                            }}
                          >
                            <Icon name="checkCircle" size="xs" />
                            <span>Review</span>
                          </button>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Apply Leave Modal */}
      {applyModal && (
        <div className="modal-backdrop" onClick={() => setApplyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Time Off Request</span>
                <h3>Apply for Leave</h3>
              </div>
              <button className="btn-icon" onClick={() => setApplyModal(false)} aria-label="Close">
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleApply}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label>Leave Type</label>
                  <select value={leaveTypeId} onChange={(e) => setLeaveTypeId(e.target.value)} required>
                    {types.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.days_per_year} days/yr)</option>
                    ))}
                  </select>
                </div>
                <div className="form-grid-2">
                  <div className="form-group">
                    <label>Start Date</label>
                    <input type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>End Date</label>
                    <input type="date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Total Working Days</label>
                  <input type="number" min="0.5" step="0.5" required value={totalDays} onChange={(e) => setTotalDays(Number(e.target.value))} />
                </div>
                <div className="form-group">
                  <label>Reason / Handover Note</label>
                  <textarea
                    required
                    rows={3}
                    placeholder="Reason for leave and emergency contact / coverage arrangement"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setApplyModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Submit Application</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Review Leave Modal */}
      {reviewModal && selectedReq && (
        <div className="modal-backdrop" onClick={() => setReviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Approval Workflow</span>
                <h3>Review Leave Request</h3>
              </div>
              <button className="btn-icon" onClick={() => setReviewModal(false)} aria-label="Close">
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleReview}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ padding: 12, background: 'var(--bg-field)', borderRadius: 8 }}>
                  <p style={{ margin: '0 0 6px 0' }}>
                    <strong>{selectedReq.first_name} {selectedReq.last_name}</strong> requested <strong>{selectedReq.total_days} days</strong> of <strong>{selectedReq.leave_type_name}</strong>.
                  </p>
                  <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                    Period: <span className="mono">{selectedReq.start_date}</span> to <span className="mono">{selectedReq.end_date}</span>
                  </p>
                  <p style={{ margin: '8px 0 0 0', fontStyle: 'italic', fontSize: 12 }}>
                    "{selectedReq.reason}"
                  </p>
                </div>

                <div className="form-group">
                  <label>Decision</label>
                  <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value as any)}>
                    <option value="approved">Approve Leave</option>
                    <option value="rejected">Reject Leave</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Remarks / Notes</label>
                  <textarea
                    rows={2}
                    placeholder="Optional notes or rejection reasons"
                    value={reviewRemarks}
                    onChange={(e) => setReviewRemarks(e.target.value)}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setReviewModal(false)}>Cancel</button>
                <button
                  type="submit"
                  className={reviewStatus === 'approved' ? 'btn-success' : 'btn-danger'}
                >
                  Confirm {reviewStatus === 'approved' ? 'Approval' : 'Rejection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

}
